import isEqual from 'lodash.isequal';
import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';

import { Favicons } from '@components/Favicons';
import Filters from '@components/Filters';
import MainHeader from '@components/MainHeader';
import MapLegend from '@components/MapLegend';
import MapWrapper from '@components/MapWrapper';
import OfficeInfo from '@components/OfficeInfo';
import Rating from '@components/Rating';
import {
  closeMobileMap,
  initializeFilters,
  onRoutesClear,
  onSelectMfc,
  openMobileMap,
  resetFilters,
  selectedRouteAction,
  toggleLegend,
  changeSearchValue,
  initializeServicesCatalog,
  toggleShowServices,
  toggleShowOfficeServices,
  selectNearestMfc,
  initializeAllData,
} from '@store/actions';
import { getMarkerStructure } from '@helpers/getMarkerStructure';

import useHeaderHeight from '@hooks/useHeaderHeight';
import { useDebouncedDeviceType } from '@hooks/useDebounceDeviceType';

import getOfficeBySlug from '@helpers/getOfficeBySlug';
import getOfficeById from '@helpers/getOfficeById';
import MobileBtnOpenHome from './private/MobileBtnOpenHome';
import MobileBtnOpenLegend from './private/MobileBtnOpenLegend';
import {
  structuredSelector,
  StructuredSelectorProps,
} from './structuredSelector';
import { FullCatalogModal, OfficeCatalogModal } from '../ServiceCatalogModal';
import MobileFiltersButton from './private/MobileFiltersButton';

import st from './Main.module.css';

const defaultHistoryState = {};

interface MainProps extends StructuredSelectorProps {
  handleSelectMfc?: (payload: any) => any;
  handlesRoutesClear?: (payload: any) => any;
  handleSelectedRoute?: (payload: any) => any;
  initializeFilters?: (payload: any) => any;
  closeMobileMap?: () => any;
  openMobileMap?: () => any;
  resetFilters?: () => any;
  initializeAllData?: () => any;
  toggleLegend?: (payload?: any) => any;
  changeSearchValue?: (payload: any) => any;
  initializeServicesCatalog?: (payload: any) => any;
  toggleShowServices?: (payload: any) => any;
  toggleShowOfficeServices?: (payload: any) => any;
  handleSelectNearestMfc?: (payload: any) => any;
}

const Main: FC<MainProps> = ({
  data,
  handleSelectMfc,
  selectedMfc,
  selectedServices,
  handlesRoutesClear,
  handleSelectedRoute,
  routeSelected,
  legendOpened,
  mobileMapOpened,
  presentationalOffices,
  nearestMfc,
  handleSelectNearestMfc,
  servicesCatalog,
  initializeFilters,
  initializeAllData,
  closeMobileMap,
  openMobileMap,
  resetFilters,
  toggleLegend,
  changeSearchValue,
  initializeServicesCatalog,
  toggleShowServices,
  toggleShowOfficeServices,
}) => {
  const servicesRef = useRef(null);
  const [headerHeight] = useHeaderHeight();
  const [device, setDevice] = useState({
    tablet: false,
    phone: false,
    desktop: true,
  });

  useDebouncedDeviceType((media) => {
    setDevice(media);
  });

  const history = useHistory();
  const { state = defaultHistoryState } = useLocation();
  const { slug } = useParams();

  const [isDetailed, setDetailed] = useState(false);
  const [isServices, setServices] = useState(false);
  const [withRating, enableRating] = useState(false);
  const [initialCatalogOpened, setInitialCatalogOpened] = useState(false);
  const [nearestOfficeSelected, setNearestOfficeSelected] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);

  const [initial, setInitial] = useState(null);

  const [showHeader, setShowHeader] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [routerMatchedOnLoad, setRouterMatchedOnLoad] = useState(false);
  const filterTypes = ['areas', 'undergrounds'];

  const onServicesCatalogOpen = useCallback(() => {
    toggleShowServices(true);
  }, [toggleShowServices]);

  const handlerSetSelectedMarker = (marker) => {
    setSelectedMarker(marker);
    if (device.phone || (device.tablet && isDetailed)) {
      toggleLegend();
    }
  };

  const onOfficeServicesCatalogOpen = () => {
    toggleShowOfficeServices(true);
  };

  useEffect(() => {
    if (!initial) {
      initializeAllData();
      setInitial(true);
    }
  }, [initial, initializeAllData]);

  useEffect(() => {
    setShowHeader(
      (device.desktop && !isDetailed) ||
        (device.tablet && !isDetailed) ||
        (device.phone && !isDetailed && !mobileMapOpened)
    );
    setShowMap(
      device.desktop || device.tablet || (device.phone && mobileMapOpened)
    );
  }, [device, isDetailed, mobileMapOpened, routeSelected]);

  useEffect(() => {
    const removeClass = () => {
      document.body.classList.remove('scroll_disabled');
    };

    if (!device.desktop && !device.tablet && mobileMapOpened) {
      document.body.classList.add('scroll_disabled');
    } else {
      removeClass();
    }

    return removeClass;
  }, [device, mobileMapOpened]);

  const isDetailMobileMapOpened =
    isDetailed && mobileMapOpened && legendOpened && !device.desktop;

  const officeId = Number(selectedMarker?.id);

  const handleSelectedMarker = useCallback(
    (payload) => {
      if (!isEqual(selectedMarker, payload)) {
        setSelectedMarker(payload);
        handleSelectMfc(payload);
      }
    },
    [handleSelectMfc, selectedMarker]
  );

  useEffect(() => {
    if (!selectedMfc.id) {
      setSelectedMarker(null);
    }
  }, [selectedMfc]);

  const reset = useCallback(() => {
    if (device.phone || (device.tablet && isDetailed)) {
      toggleLegend();
    } else {
      handleSelectedMarker(null);
    }

    handlesRoutesClear(true);
    filterTypes.map((type) =>
      changeSearchValue({ fieldName: type, value: '' })
    );
    setDetailed(false);
  }, [
    device.phone,
    device.tablet,
    isDetailed,
    handlesRoutesClear,
    filterTypes,
    toggleLegend,
    handleSelectedMarker,
    changeSearchValue,
  ]);

  useEffect(() => {
    if (!slug) {
      setDetailed(false);
    }
    if (
      Array.isArray(presentationalOffices) &&
      presentationalOffices.length > 0 &&
      slug
    ) {
      const { undergrounds } = data;
      const office = getOfficeBySlug(presentationalOffices, slug, undergrounds);

      if (office) {
        handleSelectedMarker(office);
        setDetailed(true);
      }
    }
  }, [handleSelectedMarker, reset, presentationalOffices, slug, data]);

  useEffect(() => {
    if (state.fromDetailed) {
      state.fromDetailed = false;
      reset();
      resetFilters();
    }
  }, [reset, handlesRoutesClear, state.fromDetailed, resetFilters]);

  useEffect(() => {
    const { offices, services, areas, undergrounds } = data;
    if (offices && services && areas && undergrounds) {
      initializeFilters({
        offices,
        services,
        areas,
        undergrounds,
      });
    }
  }, [data, initializeFilters]);

  useEffect(() => {
    if (data.servicesCatalog) initializeServicesCatalog(data.servicesCatalog);
  }, [data.servicesCatalog, initializeServicesCatalog, servicesCatalog]);

  const toggleMobileFilters = () => {
    setSelectedMarker(selectedMarker ? selectedMfc : null);
    toggleLegend();
  };

  const showMobileMap = useCallback(() => {
    toggleLegend(true);
    openMobileMap();
  }, [openMobileMap, toggleLegend]);

  const hideMobileMap = useCallback(() => {
    toggleLegend(false);
    closeMobileMap();
  }, [closeMobileMap, toggleLegend]);

  useEffect(() => {
    if (state.fromDetailed) {
      state.fromDetailed = false;
      history.replace({ ...history.location, state });
    }
  }, [history, state]);

  useEffect(() => {
    if (!routerMatchedOnLoad) {
      setRouterMatchedOnLoad(true);

      if (state.showMobileMapFromDetailed) {
        state.showMobileMapFromDetailed = false;
        history.replace({ ...history.location, state });
      }
    }

    if (state.showMobileMapFromDetailed) {
      showMobileMap();
    } else {
      hideMobileMap();
    }
  }, [
    history,
    showMobileMap,
    hideMobileMap,
    routerMatchedOnLoad,
    setRouterMatchedOnLoad,
    state,
  ]);

  useEffect(() => {
    if (nearestMfc) {
      const marker = getMarkerStructure(
        nearestMfc,
        presentationalOffices,
        data?.undergrounds
      );

      setSelectedMarker(marker);
      handleSelectMfc(marker);
      setNearestOfficeSelected(true);
    }
  }, [data.undergrounds, handleSelectMfc, nearestMfc, presentationalOffices]);

  useEffect(() => {
    if (selectedMarker && selectedMarker.id === nearestMfc) {
      setNearestOfficeSelected(false);
      handleSelectNearestMfc(null);
    }
  }, [handleSelectNearestMfc, nearestMfc, selectedMarker]);

  const showRatingButton = device.desktop || device.tablet || !routeSelected;

  useEffect(() => {
    if (isServices && servicesRef.current) {
      setServices(false);
      window.scrollTo({
        top: servicesRef.current.offsetTop,
        behavior: 'smooth',
      });
    }
  }, [isServices]);

  useEffect(() => {
    if (
      window &&
      window.location &&
      window.location.search &&
      window.location.search.includes('?catalog') &&
      servicesCatalog.length &&
      !initialCatalogOpened
    ) {
      setTimeout(() => {
        onServicesCatalogOpen();
        setInitialCatalogOpened(true);
      }, 0);
    }
  }, [initialCatalogOpened, onServicesCatalogOpen, servicesCatalog]);

  const numberOfPoints = data?.offices?.length;
  const mainClassName = `${isDetailed ? st.mainDetailed : st.main} ${
    isDetailMobileMapOpened ? st.mainDetailedMapOpened : ''
  }`;
  const office = officeId ? getOfficeById(data.offices, officeId, data) : null;

  const handleMapCloseClick = useCallback(() => {
    if (routeSelected) {
      toggleLegend();
      handleSelectedRoute(null);
    } else {
      history.push(history.location.pathname, {
        showMobileMapFromDetailed: false,
      });
      setSelectedMarker(null);
      handleSelectedRoute(null);
      onSelectMfc(null);
      window.scrollTo(0, 0);
    }
  }, [routeSelected, handleSelectedRoute, toggleLegend, history]);

  useEffect(() => {
    if (isDetailed) {
      const scriptEl = document.querySelector('#mainBreadcrumbsSchema');
      if (scriptEl) {
        scriptEl.remove();
      }
    } else {
      const scriptEl = document.querySelector('#mainBreadcrumbsSchema');
      if (scriptEl) {
        return;
      }
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'mainBreadcrumbsSchema';
      script.innerHTML = `{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Услуги",
            "item": "https://www.mos.ru/uslugi/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Центры государственных услуг г. Москвы «Мои документы»",
            "item": "https://www.mos.ru/services/centry-gosudarstvennyh-uslug/"
          }
        ]
      }`;
      document.head.appendChild(script);
    }
  }, [isDetailed]);

  return (
    <>
      <div className={st.container}>
        <Helmet>
          <title>
            Центры государственных услуг г. Москвы «Мои документы» на карте /
            Официальный сайт Мэра Москвы
          </title>
          <meta
            name="description"
            content="Центры госуслуг – это комфортные офисы в каждом районе Москвы, где ежедневно быстро и удобно можно получить государственные услуги. 99 % услуг предоставляются без привязки к месту регистрации."
          />
          <meta
            name="keywords"
            content="мфц, центр госуслуг москвы, мои документы, мфц москвы, мфц официальный сайт"
          />
          <meta
            property="og:title"
            content="Центры государственных услуг г. Москвы «Мои документы»"
          />
          <meta
            property="og:description"
            content="Центры госуслуг – это комфортные офисы в каждом районе Москвы, где ежедневно быстро и удобно можно получить государственные услуги. 99 % услуг предоставляются без привязки к месту регистрации."
          />
          <meta
            property="og:image"
            content="https://www.mos.ru/open-graph/v1/frontend/services/centry-gosudarstvennyh-uslug/"
          />
          <meta
            property="og:url"
            content="https://www.mos.ru/services/centry-gosudarstvennyh-uslug/"
          />
          <Favicons />
        </Helmet>

        {showHeader && (
          <MainHeader
            phone={device.phone}
            showMap={showMobileMap}
            numberOfPoints={numberOfPoints}
            onServicesCatalogOpen={onServicesCatalogOpen}
            presentationalOfficesCount={presentationalOffices.length}
          />
        )}
        {(showMap || isDetailed) && (
          <div className={mainClassName} id="MFC-Map_Wrapper">
            <MapWrapper
              showingNearest={nearestOfficeSelected}
              onActivePointClick={() =>
                device.phone ? handleSelectedRoute(true) : undefined
              }
              isDetailed={isDetailed}
              headerHeight={headerHeight}
              mobileMapOpened={mobileMapOpened}
              offices={presentationalOffices}
              undergrounds={data?.undergrounds}
              isFilterApplied={presentationalOffices?.length < numberOfPoints}
              selectedMarker={selectedMarker}
              setSelectedMarker={handlerSetSelectedMarker}
              withRating={withRating}
              reset={reset}
            />
            <MapLegend big={isDetailed}>
              {officeId ? (
                <OfficeInfo
                  isDetailed={isDetailed}
                  office={office}
                  service={selectedServices[0]}
                  onServicesCatalogOpen={onOfficeServicesCatalogOpen}
                  onBack={reset}
                  phone={device.phone}
                />
              ) : (
                <Filters
                  phone={device.phone}
                  service={selectedServices[0]}
                  onClose={toggleMobileFilters}
                />
              )}
            </MapLegend>
            {device.phone && !isDetailed && (
              <MobileFiltersButton onClick={toggleMobileFilters} />
            )}
            {showRatingButton && !isDetailed && (
              <Rating
                phone={device.phone}
                handleEnableRating={enableRating}
                enabled={withRating}
              />
            )}
            {officeId && (
              <MobileBtnOpenLegend
                show={isDetailed || !!routeSelected}
                onClick={() => handleSelectedRoute(true)}
              />
            )}
            <MobileBtnOpenHome onClick={handleMapCloseClick} />
          </div>
        )}
      </div>
      <FullCatalogModal
        initialCatalogOpened={initialCatalogOpened}
        replaceRoute={history.replace}
      />
      <OfficeCatalogModal />
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  initializeAllData: () => dispatch(initializeAllData()),
  closeMobileMap: () => dispatch(closeMobileMap()),
  openMobileMap: () => dispatch(openMobileMap()),
  initializeFilters: (payload) => dispatch(initializeFilters(payload)),
  handlesRoutesClear: () => dispatch(onRoutesClear()),
  handleSelectMfc: (payload) => dispatch(onSelectMfc(payload)),

  resetFilters: () => dispatch(resetFilters()),
  handleSelectedRoute: (payload) => dispatch(selectedRouteAction(payload)),
  toggleLegend: (payload) => dispatch(toggleLegend(payload)),
  changeSearchValue: (payload) => dispatch(changeSearchValue(payload)),
  initializeServicesCatalog: (payload) =>
    dispatch(initializeServicesCatalog(payload)),
  toggleShowServices: (payload) => dispatch(toggleShowServices(payload)),
  toggleShowOfficeServices: (payload) =>
    dispatch(toggleShowOfficeServices(payload)),
  handleSelectNearestMfc: (payload) => dispatch(selectNearestMfc(payload)),
});

export default connect(structuredSelector, mapDispatchToProps)(Main);
